/**
 * Cross-domain login handoff — bam-frontend (DAM) INGEST side.
 *
 * If the URL carries a `#bam_sso=<code>` handed off by mp web (see
 * bam-api-infrastructure/auth/handoff.js and mp/src/dux/auth/ssoHandoff.ts),
 * redeem it and seed this app's Amplify (classic) session so the DAM adopts the
 * user's session without a re-login — for the tenant's paired admin domain only.
 *
 * Per-tenant isolation and exclusion of the tenant-agnostic admin.bam.zone are
 * enforced HERE, fail-closed: the redeemed token's `custom:tenant` key must
 * equal the tenant this `admin-<key>.bam.zone` host is for. `admin.bam.zone`
 * matches no key and is rejected; a rebranded admin host (whose subdomain isn't
 * the tenant key) also fails closed and simply falls back to normal login.
 *
 * Only seeds when there is no existing session (login-CSRF mitigation). Purely
 * best-effort: any failure leaves the DAM to its normal login flow.
 *
 * Babel 6 build target: no optional chaining / nullish coalescing.
 */

import { Auth } from 'aws-amplify'
import {
  ADMIN_HOST_TENANT_KEY,
  APP_URL_BY_TENANT,
} from '../../config/tenants.generated'
import {
  CookieStorage,
  CognitoUserPool,
  CognitoUser,
  CognitoUserSession,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken,
} from 'amazon-cognito-identity-js'

function readHandoffCode() {
  var hash = window.location.hash || ''
  var m = /[#&]bam_sso=([^&]+)/.exec(hash)
  return m ? decodeURIComponent(m[1]) : null
}

// Remove bam_sso from the fragment (keeping any other hash content) without
// adding a history entry, so the code can't be reused or seen in the address bar.
function stripHandoffFromUrl() {
  var loc = window.location
  var hash = (loc.hash || '').replace(/^#/, '')
  var kept = hash
    .split('&')
    .filter(function (p) {
      return p.indexOf('bam_sso=') !== 0
    })
    .join('&')
  var newUrl = loc.pathname + loc.search + (kept ? '#' + kept : '')
  window.history.replaceState({}, document.title, newUrl)
}

/*
 * Tenant key from a `custom:tenant` claim.
 *
 * The claim is a tenant PARTITION key -- the tenantId plus '-' and a small
 * number -- so the suffix has to come off before the '_' split, or the six
 * tenants whose tenantId is bare yield 'showme-4' where the key is 'showme'
 * and every comparison against it fails closed.
 *
 * Keys with an underscore of their own (qa_distributor, qa_manufacturer) still
 * collapse to 'qa' here, as they do everywhere else in the codebase. Both are
 * qa-stage test tenants, and the result only ever fails an equality check.
 */
function tenantKeyFromClaim(claim) {
  if (!claim) return ''
  return String(claim).replace(/-\d+$/, '').split('_')[0]
}

function decodeJwt(token) {
  try {
    var b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(window.atob(b64))
  } catch (e) {
    return {}
  }
}

// Admin hosts whose subdomain is NOT the tenant key (rebrands) -- the token's
// custom:tenant carries the real key, so map these explicitly. GENERATED from
// tenants.json: the hand-maintained copy was missing admin-btahelp and
// admin-promach, so the handoff had been failing closed for those two tenants.

// admin-<key>.bam.zone -> <key> (with rebrand overrides); admin.bam.zone /
// anything else -> '' (reject, fail closed).
function hostTenantKey() {
  var host = window.location.hostname
  if (ADMIN_HOST_TENANT_KEY[host]) return ADMIN_HOST_TENANT_KEY[host]
  var m = /^admin-([^.]+)\.bam\.zone$/.exec(host)
  return m ? m[1] : ''
}

// Must match configureAmplify's cookieStorage (dux/modules/utils.js) so
// Amplify's Auth reads the same cookies we write.
function makeStorage() {
  return new CookieStorage({
    domain: window.location.hostname,
    path: '/',
    expires: 1,
    sameSite: 'lax',
    secure: false,
  })
}

/**
 * @param {object} config fetched config.json ({ appClient, userPool, baseUrl })
 * @returns {Promise<boolean>} true if a session was seeded
 */
export async function ingestHandoff(config) {
  if (typeof window === 'undefined') return false
  var code = readHandoffCode()
  if (!code) return false
  // Strip immediately, even if the steps below fail.
  stripHandoffFromUrl()

  try {
    var clientId = config && config.appClient
    var userPoolId = config && config.userPool
    var baseUrl = config && config.baseUrl
    if (!clientId || !userPoolId || !baseUrl) return false

    var storage = makeStorage()

    // Don't clobber an existing session.
    var existing = storage.getItem(
      'CognitoIdentityServiceProvider.' + clientId + '.LastAuthUser',
    )
    if (existing) return false

    var res = await fetch(baseUrl + '/api/auth/handoff/redeem', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: code }),
    })
    if (!res.ok) return false
    var data = await res.json()
    var idToken = data && data.idToken
    var accessToken = data && data.accessToken
    var refreshToken = data && data.refreshToken
    if (!idToken || !accessToken || !refreshToken) return false

    // Per-tenant + agnostic gate (fail closed).
    var claims = decodeJwt(idToken)
    var tokenKey = tenantKeyFromClaim(claims['custom:tenant'])
    var expected = hostTenantKey()
    if (!expected || tokenKey !== expected) return false

    // Seed the classic Amplify session into the same CookieStorage Auth reads.
    var username = claims['cognito:username'] || claims.sub
    if (!username) return false
    var pool = new CognitoUserPool({
      UserPoolId: userPoolId,
      ClientId: clientId,
      Storage: storage,
    })
    var user = new CognitoUser({
      Username: username,
      Pool: pool,
      Storage: storage,
    })
    var session = new CognitoUserSession({
      IdToken: new CognitoIdToken({ IdToken: idToken }),
      AccessToken: new CognitoAccessToken({ AccessToken: accessToken }),
      RefreshToken: new CognitoRefreshToken({ RefreshToken: refreshToken }),
    })
    user.setSignInUserSession(session)
    return true
  } catch (e) {
    return false
  }
}

// ---- EMIT side (admin -> mp) -----------------------------------------------
// Wired to the header's "App" button (BamHeader / BamHeaderContainer).

// Stage -> the shared mp web front door, for tenants with no domain of their
// own (sharedHosts) and for the qa stage, where mp web is dev.bam.zone.
var SHARED_APP_URL = {
  app: 'https://app.bam.zone',
  qa: 'https://dev.bam.zone',
  dev: 'https://dev.bam.zone',
}

/**
 * The mp web portal for a `custom:tenant` claim, or the stage's shared front
 * door when the tenant has no domain of its own.
 *
 * @param {string} tenantClaim the token's custom:tenant (a partition key)
 * @param {string} stage config.stage
 * @returns {string|null}
 */
export function appUrlForTenant(tenantClaim, stage) {
  var fallback = SHARED_APP_URL[stage] || null
  return APP_URL_BY_TENANT[tenantKeyFromClaim(tenantClaim)] || fallback
}

/**
 * The tenant's mp web portal for the signed-in session.
 *
 * Resolved from the token's `custom:tenant` rather than from the host, because
 * most tenants have no admin-<key> subdomain at all and sign in on the
 * tenant-agnostic admin.bam.zone -- there is nothing in the host to read.
 *
 * @param {object} config fetched config.json ({ stage })
 * @returns {Promise<string|null>} absolute url, or null if it can't be resolved
 */
export async function getAppUrl(config) {
  var stage = config && config.stage
  try {
    var session = await Auth.currentSession()
    var claims = decodeJwt(session.getIdToken().getJwtToken())
    return appUrlForTenant(claims['custom:tenant'], stage)
  } catch (e) {
    return appUrlForTenant(null, stage)
  }
}

/**
 * Mint a one-time handoff code for the current DAM session, or null on failure.
 * @param {string} baseUrl e.g. config.baseUrl (https://api.bam.zone)
 * @returns {Promise<string|null>}
 */
export async function getHandoffCode(baseUrl) {
  if (typeof window === 'undefined' || !baseUrl) return null
  try {
    var session = await Auth.currentSession()
    // Classic sessions expose all three tokens directly.
    var idToken = session.getIdToken().getJwtToken()
    var accessToken = session.getAccessToken().getJwtToken()
    var refreshToken = session.getRefreshToken().getToken()
    if (!idToken || !accessToken || !refreshToken) return null

    var res = await fetch(baseUrl + '/api/auth/handoff', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        idToken: idToken,
        accessToken: accessToken,
        refreshToken: refreshToken,
      }),
    })
    if (!res.ok) return null
    var data = await res.json()
    return data && data.code ? data.code : null
  } catch (e) {
    return null
  }
}

function withHandoff(url, code) {
  if (!code) return url
  return url.indexOf('#') !== -1
    ? url + '&bam_sso=' + code
    : url + '#bam_sso=' + code
}

/**
 * Open the tenant's mp web app carrying the current session. Opens the tab
 * synchronously (within the click gesture) to dodge popup blocking, then
 * navigates it once the code is minted; same-tab fallback if blocked.
 * @param {string} targetUrl the paired mp URL to open
 * @param {string} baseUrl config.baseUrl for the handoff mint
 */
export function openMpWithHandoff(targetUrl, baseUrl) {
  if (typeof window === 'undefined') return
  var w = window.open('', '_blank')
  getHandoffCode(baseUrl).then(function (code) {
    var url = withHandoff(targetUrl, code)
    if (w) w.location.href = url
    else window.location.href = url
  })
}

var REDIRECTED_KEY = 'bam_app_redirected'

/**
 * Leave the DAM for this session's tenant app, in the same tab so Back doesn't
 * return here. Stays in the DAM if the session can't be carried over, and tries
 * once per tab so a bounce back from the app can't become a loop.
 *
 * @param {object} config fetched config.json ({ baseUrl, stage })
 */
export async function redirectToApp(config) {
  if (typeof window === 'undefined') return
  try {
    if (window.sessionStorage.getItem(REDIRECTED_KEY)) return
    var results = await Promise.all([
      getAppUrl(config),
      getHandoffCode(config && config.baseUrl),
    ])
    if (!results[0] || !results[1]) return
    window.sessionStorage.setItem(REDIRECTED_KEY, '1')
    window.location.replace(withHandoff(results[0], results[1]))
  } catch (e) {
    // Staying in the DAM is always a safe outcome
  }
}

/**
 * Open this session's tenant app, resolving the destination from the token.
 *
 * The tab is opened before anything is awaited, for the same popup-blocker
 * reason as above. A failed handoff mint is not fatal: the app opens anyway and
 * asks the user to sign in.
 *
 * @param {object} config fetched config.json ({ baseUrl, stage })
 */
export function openAppWithHandoff(config) {
  if (typeof window === 'undefined') return
  var w = window.open('', '_blank')
  Promise.all([getAppUrl(config), getHandoffCode(config && config.baseUrl)])
    .then(function (results) {
      return { url: results[0], code: results[1] }
    })
    .catch(function () {
      return { url: null, code: null }
    })
    .then(function (resolved) {
      if (!resolved.url) {
        // Nowhere to send them -- close the blank tab rather than leave it.
        if (w) w.close()
        return
      }
      var url = withHandoff(resolved.url, resolved.code)
      if (w) w.location.href = url
      else window.location.href = url
    })
}
